import {
  BottomSheet,
  CardWithIntent,
  CbhIcon,
  Divider,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGetShift } from "@src/appV2/Shifts/Shift/api/useGetShift";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";

import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";
import { TopRightCloseButton } from "../components/TopRightCloseButton";
import { useGetWorkplaceProfile } from "../Workplace/useGetWorkplaceProfile";
import { type ChartingNoncompliance } from "./api/schema";
import { ChartingNoncomplianceText } from "./ChartingNoncomplianceText";
import { StructuredInfoSection } from "./components/StructuredInfoSection";
import { getChartingNoteDisplayText } from "./utils/displayTextUtils";
import { chartingDeadlineHasPassed } from "./utils/helpers";

interface ChartingNoncomplianceBottomSheetProps {
  chartingNoncomplianceModalState: UseModalState;
  chartingNoncomplianceRectifiedModalState: UseModalState;
  workerId: string;
  chartingNoncompliance: ChartingNoncompliance;
}

export function ChartingNoncomplianceBottomSheet({
  chartingNoncomplianceModalState,
  chartingNoncomplianceRectifiedModalState,
  workerId,
  chartingNoncompliance,
}: ChartingNoncomplianceBottomSheetProps) {
  const {
    attributes: {
      deadlineToChart,
      modalContent: {
        preDeactivationHeader,
        preDeactivationSubtext,
        deactivatedHeader,
        deactivatedSubtext,
      },
      deadlineToReview,
    },
    relationships: {
      workplace: {
        data: { id: workplaceId },
      },
      shift: {
        data: { id: shiftId },
      },
    },
  } = chartingNoncompliance;

  const chartingNotesDisplayText = getChartingNoteDisplayText(chartingNoncompliance);

  const { data: workplaceData } = useGetWorkplaceProfile(
    { workplaceId },
    { enabled: isDefined(workplaceId) }
  );

  const { data: shiftData } = useGetShift(shiftId, { enabled: isDefined(shiftId) });
  const shift = shiftData?.response;
  const { start, name } = shift ?? {};
  const logMetadata = {
    timestamp: new Date().toISOString(),
    workerId,
    workplaceId,
  };

  if (
    isDefined(deadlineToReview) &&
    !chartingDeadlineHasPassed(deadlineToReview) &&
    chartingNoncomplianceModalState.modalIsOpen
  ) {
    chartingNoncomplianceModalState.closeModal();
    chartingNoncomplianceRectifiedModalState.openModal();
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        modalState={chartingNoncomplianceModalState}
        canDismiss={false}
        footer={
          <DialogFooter orientation="vertical">
            <Button
              size="large"
              sx={{ width: "100%" }}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_TASK_COMPLETED, logMetadata);
                chartingNoncomplianceModalState.closeModal();
                chartingNoncomplianceRectifiedModalState.openModal();
              }}
            >
              I returned — Verify charting
            </Button>
          </DialogFooter>
        }
      >
        <DialogContent sx={{ px: 4 }}>
          <TopRightCloseButton onClick={chartingNoncomplianceModalState.closeModal} />
        </DialogContent>
        <Box
          sx={{
            paddingX: 5,
            paddingTop: 8,
            paddingBottom: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <ChartingNoncomplianceText
            text={
              chartingDeadlineHasPassed(deadlineToChart) ? deactivatedHeader : preDeactivationHeader
            }
            deadlineToChart={deadlineToChart}
            variant="h3"
            sx={{ fontWeight: "bold", textAlign: "center", paddingX: 8 }}
          />

          <Stack spacing={6} sx={{ paddingY: 4 }}>
            {isDefined(workplaceData) && isDefined(workplaceData.name) && (
              <StructuredInfoSection
                title="Workplace"
                sx={{ paddingY: 4, paddingLeft: 8, paddingRight: 4 }}
              >
                <Text bold variant="body1" sx={{ color: (theme) => theme.palette.text.primary }}>
                  {workplaceData.name}
                </Text>
                {isDefined(start) && isDefined(name) && (
                  <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                    Incomplete charting from {format(new Date(start), "M/d")} {name.toUpperCase()}{" "}
                    shift
                  </Text>
                )}
              </StructuredInfoSection>
            )}

            {isDefined(workplaceData) &&
              isDefined(workplaceData.name) &&
              isDefined(chartingNotesDisplayText) && <Divider />}

            {isDefined(chartingNotesDisplayText) && (
              <StructuredInfoSection
                title="Note"
                sx={{ paddingY: 4, paddingLeft: 8, paddingRight: 4 }}
              >
                <Text variant="body1" sx={{ color: (theme) => theme.palette.text.primary }}>
                  {chartingNotesDisplayText}
                </Text>
              </StructuredInfoSection>
            )}

            {((isDefined(workplaceData) && isDefined(workplaceData.name)) ||
              isDefined(chartingNotesDisplayText)) && <Divider />}

            <StructuredInfoSection
              title="Impact"
              sx={{ paddingY: 4, paddingLeft: 8, paddingRight: 4 }}
            >
              <Text variant="body1" sx={{ color: (theme) => theme.palette.text.primary }}>
                {chartingDeadlineHasPassed(deadlineToChart)
                  ? deactivatedSubtext
                  : preDeactivationSubtext}
              </Text>
            </StructuredInfoSection>
            <CardWithIntent
              intent="neutral"
              sx={{
                backgroundColor: (theme) => theme.palette.intent?.neutral.background,
                color: (theme) => theme.palette.intent?.neutral.text,
                padding: 4,
                marginX: 4,
              }}
            >
              <Stack direction="row" spacing={3} alignItems="top" sx={{ padding: 1 }}>
                <CbhIcon type="map-pin" size="small" />
                <Text variant="body2">
                  You can verify charting as complete when you return to the workplace
                </Text>
              </Stack>
            </CardWithIntent>
          </Stack>
        </Box>
      </BottomSheet>
    </ThemeProvider>
  );
}
